import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import Navbar from '../components/landing/Navbar';
import Footer from '../components/landing/Footer';
import { LandingAuthProvider } from '../contexts/LandingAuthContext';
import { LandingAppProvider } from '../contexts/LandingAppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChangelogPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/changelog/list?limit=50`);
        setEntries(res.data?.entries || []);
      } catch (err) {
        setError('Could not load changelog.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <LandingAuthProvider>
      <LandingAppProvider>
        <div className="f500-page min-h-screen" data-testid="changelog-page">
          <Navbar />

          {/* Hero */}
          <section className="border-b border-[var(--isp-glass-border)]">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 pt-20 lg:pt-28 pb-12">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-xs text-[var(--isp-body)] hover:text-[var(--isp-heading)] mb-8 transition-colors"
                style={{ fontFamily: 'var(--sf-text)' }}
                data-testid="changelog-back-home"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
              </Link>
              <p className="f500-eyebrow mb-6">— Product / Changelog</p>
              <motion.h1
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="f500-display text-5xl sm:text-6xl lg:text-7xl"
              >
                What we<br />shipped.
              </motion.h1>
              <p className="f500-body text-base mt-6 max-w-xl">
                A running log of releases, fixes, and small details we sweat over. Newest first.
              </p>
            </div>
          </section>

          {/* Timeline */}
          <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16">
            {loading ? (
              <div className="flex items-center gap-2 text-[var(--isp-body)]" data-testid="changelog-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : error ? (
              <div className="border-l-4 border-[var(--f500-red,#FF3B30)] bg-[#FFF5F4] p-6 text-[#7A1813]" data-testid="changelog-error">
                {error}
              </div>
            ) : entries.length === 0 ? (
              <div className="border border-[var(--isp-glass-border)] p-12 text-center" data-testid="changelog-empty">
                <Sparkles className="w-8 h-8 mx-auto mb-3 text-[var(--isp-muted)]" strokeWidth={1.5} />
                <p className="f500-body text-sm">No release notes yet — check back soon.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-t border-[var(--isp-glass-border)]">
                {entries.map((entry, idx) => (
                  <motion.article
                    key={entry.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ delay: idx * 0.05, duration: 0.4 }}
                    className="lg:col-span-12 grid grid-cols-1 lg:grid-cols-12 gap-0 border-b border-[var(--isp-glass-border)] py-8 lg:py-10"
                    data-testid={`changelog-entry-${entry.id}`}
                  >
                    {/* Meta column */}
                    <div className="lg:col-span-3 mb-4 lg:mb-0 lg:pr-8">
                      <p className="f500-eyebrow mb-2">{entry.version}</p>
                      <p className="text-xs text-[var(--isp-muted)]" style={{ fontFamily: 'var(--sf-text)' }}>
                        {entry.published_at ? new Date(entry.published_at).toLocaleDateString(undefined, {
                          year: 'numeric', month: 'long', day: 'numeric',
                        }) : ''}
                      </p>
                    </div>

                    {/* Content column */}
                    <div className="lg:col-span-9 lg:border-l lg:border-[var(--isp-glass-border)] lg:pl-8">
                      <h2
                        className="text-2xl sm:text-3xl mb-3 leading-[1.1] tracking-[-0.025em]"
                        style={{ fontFamily: 'var(--sf-display)', fontWeight: 800, color: 'var(--isp-heading)' }}
                      >
                        {entry.title}
                      </h2>
                      {entry.body && (
                        <p className="f500-body text-base mb-6 max-w-2xl">{entry.body}</p>
                      )}
                      {Array.isArray(entry.highlights) && entry.highlights.length > 0 && (
                        <ul className="space-y-3">
                          {entry.highlights.map((h, i) => (
                            <li
                              key={i}
                              className="flex gap-4 pb-3 border-b border-[var(--isp-glass-border)] last:border-b-0 last:pb-0"
                            >
                              <span className="f500-mono text-xs text-[var(--isp-muted)] tabular-nums pt-0.5 shrink-0">
                                {String(i + 1).padStart(2, '0')}
                              </span>
                              <div>
                                <p className="text-sm font-semibold text-[var(--isp-heading)]" style={{ fontFamily: 'var(--sf-text)' }}>
                                  {h.title}
                                </p>
                                {h.body && (
                                  <p className="text-sm text-[var(--isp-body)] mt-1 leading-relaxed">{h.body}</p>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </section>

          {/* CTA */}
          <section className="bg-[var(--f500-ink)] text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                <div className="md:col-span-8">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60 mb-4">— Subscribe</p>
                  <h2 className="f500-h1 text-white text-3xl sm:text-5xl">
                    Want updates in<br />your inbox?
                  </h2>
                  <p className="text-white/70 text-base mt-4 max-w-2xl" style={{ fontFamily: 'var(--sf-text)' }}>
                    We email a short summary on every major release — no spam, no marketing fluff.
                  </p>
                </div>
                <div className="md:col-span-4 md:text-right">
                  <Link
                    to="/settings?tab=notifications"
                    className="f500-btn f500-btn-inverse"
                    data-testid="changelog-subscribe-cta"
                  >
                    Manage notifications <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <Footer />
        </div>
      </LandingAppProvider>
    </LandingAuthProvider>
  );
};

export default ChangelogPage;
